import { type MouseEvent, useCallback, useEffect, useRef, useState } from "react";
import { type Poll, startPoll } from "./poll";

export const REFRESH_MS = 60_000;

export function useTitle(title: string) {
  useEffect(() => {
    document.title = `${title} · Xflow`;
  }, [title]);
}

const CRUMB = "xflow-crumb";
let crumb: string | null = null;

/** Name the record that this page shows, as the last part of the breadcrumb. */
export function useCrumb(label: string | null) {
  useEffect(() => {
    crumb = label;
    dispatchEvent(new Event(CRUMB));
    return () => {
      crumb = null;
      dispatchEvent(new Event(CRUMB));
    };
  }, [label]);
}

export function useCrumbLabel(): string | null {
  const [label, setLabel] = useState(crumb);
  useEffect(() => {
    const change = () => setLabel(crumb);
    addEventListener(CRUMB, change);
    return () => removeEventListener(CRUMB, change);
  }, []);
  return label;
}

export function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), REFRESH_MS);
    return () => clearInterval(timer);
  }, []);
  return now;
}

/** Start `load` with startPoll and REFRESH_MS. The function that this hook returns starts a new `load` immediately. */
export function usePoll(load: (signal: AbortSignal) => Promise<void>, enabled = true): () => void {
  const poll = useRef<Poll | null>(null);
  useEffect(() => {
    if (!enabled) return;
    const started = startPoll(load, REFRESH_MS);
    poll.current = started;
    return () => {
      started.stop();
      if (poll.current === started) poll.current = null;
    };
  }, [load, enabled]);
  return useCallback(() => {
    if (poll.current) poll.current.now();
    else void load(new AbortController().signal);
  }, [load]);
}

export function go(event: MouseEvent<HTMLAnchorElement>) {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  history.pushState(null, "", event.currentTarget.href);
  dispatchEvent(new PopStateEvent("popstate"));
}

/** Calls `set` with the query string on each popstate. It returns the stop function. */
export function followSearch(set: (search: string) => void, target: EventTarget = window, search: () => string = () => location.search): () => void {
  const update = () => set(search());
  target.addEventListener("popstate", update);
  return () => target.removeEventListener("popstate", update);
}

/** A value from the query string. It changes when a link or Back opens the same page with a new query. */
export function useSearchState<T>(read: (search: string) => T) {
  const [value, setValue] = useState(() => read(location.search));
  useEffect(() => followSearch((search) => setValue(read(search))), [read]);
  return [value, setValue] as const;
}

/** Scroll to the section of the hash, and keep it in view while the sections above it load. */
export function useSection(hash: string, shown: string) {
  useEffect(() => {
    const section = hash.slice(1) && document.getElementById(hash.slice(1));
    if (!section) return;
    const place = () => section.scrollIntoView({ block: "start" });
    place();
    section.focus({ preventScroll: true });
    const page = section.closest("main");
    if (!page || typeof ResizeObserver === "undefined") return;
    // The sections above load late and push the target down. Keep it in view until the user acts.
    const watch = new ResizeObserver(place);
    watch.observe(page);
    const acts = ["wheel", "touchstart", "pointerdown", "keydown"] as const;
    const stop = () => {
      watch.disconnect();
      acts.forEach((name) => window.removeEventListener(name, stop));
    };
    acts.forEach((name) => window.addEventListener(name, stop, { passive: true }));
    const timer = window.setTimeout(stop, 5000);
    return () => {
      window.clearTimeout(timer);
      stop();
    };
  }, [hash, shown]);
}
